import { useNavigate, useParams } from 'react-router-dom-v6'
import { useTranslation } from 'react-i18next'
import { Skeleton, Tabs, TabsContent, TabsList, TabsTrigger } from '@clouway/ui'

import { AllowedScopesEditor } from '@/components/AllowedScopesEditor'
import { Permission, hasPermission, useSession } from '@/features/auth'
import { ServiceAccountAccessTab } from '../components/ServiceAccountAccessTab'
import { ServiceAccountForm } from '../components/ServiceAccountForm'
import {
  useServiceAccountAllowedScopes,
  useServiceAccounts,
  usePutServiceAccountAllowedScopes,
  useUpdateServiceAccount,
} from '../hooks'
import { toInput } from '../transform/serviceAccountTransformer'
import type { ServiceAccountInput } from '../types'

export function EditServiceAccount() {
  const { t } = useTranslation('feature')
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: accounts, isLoading } = useServiceAccounts()
  const { mutateAsync: update } = useUpdateServiceAccount()
  const account = accounts?.find((candidate) => candidate.id === id)
  const { access } = useSession()
  const canReadAccess = hasPermission(access, Permission.serviceAccounts.get)
  const canWriteAccess = hasPermission(access, Permission.serviceAccounts.update)
  // Called unconditionally (hooks can't be gated behind the early return
  // below), but the query itself is disabled without read access — the id is
  // blanked instead so the SPA never fires a request the server would 401.
  const { data: allowedScopes, isLoading: allowedScopesLoading } = useServiceAccountAllowedScopes(canReadAccess ? id : '')
  const { mutateAsync: putAllowedScopes } = usePutServiceAccountAllowedScopes(id)

  if (isLoading || !account) return <Skeleton className="mx-auto h-80 w-full max-w-2xl" />

  const onSubmit = async (input: ServiceAccountInput) => {
    await update({ id, input })
    navigate('/service-accounts')
  }

  const settings = (
    <ServiceAccountForm mode="edit" initialValues={toInput(account)} clientEmail={account.clientEmail} onSubmit={onSubmit} />
  )

  // No serviceAccounts.get: the caller can't reach the access tab's endpoints
  // at all, so skip the tab chrome entirely rather than show a tab that 401s
  // (same rule EditUser applies for its Access tab).
  if (!canReadAccess) {
    return settings
  }

  return (
    <Tabs defaultValue="settings" className="mx-auto w-full max-w-2xl">
      <TabsList>
        <TabsTrigger value="settings">{t('serviceAccount.edit.tabs.settings')}</TabsTrigger>
        <TabsTrigger value="access">{t('serviceAccount.edit.tabs.access')}</TabsTrigger>
      </TabsList>
      <TabsContent value="settings">{settings}</TabsContent>
      <TabsContent value="access" className="flex flex-col gap-4">
        <ServiceAccountAccessTab serviceAccountId={id} canWrite={canWriteAccess} />
        <AllowedScopesEditor
          allowedScopeIds={allowedScopes?.map((scope) => scope.id)}
          isLoading={allowedScopesLoading}
          onSave={putAllowedScopes}
          readOnly={!canWriteAccess}
        />
      </TabsContent>
    </Tabs>
  )
}
